import React, { useState } from 'react';
import { useSwipe } from '../hooks/useSwipe';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, ChevronLeft, Building2, FileCheck, Briefcase } from 'lucide-react';

const phoneNumber = '5555981013037'; // WhatsApp number

const banners = [
  {
    id: 1,
    title: "Consultoria Empresarial",
    subtitle: "Apoiamos sua empresa com consultoria empresarial estratégica, oferecendo orientação prática e personalizada para fortalecer a gestão e impulsionar resultados.",
    cta: "Falar com Consultor",
    whatsappMessage: encodeURIComponent('Olá! Gostaria de saber mais sobre a abertura da minha empresa com a TALC Assessoria Contábil.'),
    icon: Building2,
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2301&auto=format&fit=crop"
  },
  {
    id: 2,
    title: "Legalização de Empresas",
    subtitle: "Cuidamos de todo o processo de legalização da sua empresa, da abertura às regularizações necessárias, para que você atue com segurança, tranquilidade e foco no seu negócio.",
    cta: "Quero Legalizar Minha Empresa",
    whatsappMessage: encodeURIComponent('Olá! Gostaria de legalizar minha empresa com a TALC Assessoria Contábil.'),
    icon: FileCheck,
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2340&auto=format&fit=crop"
  }
];

const Hero = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
  };

  const handleCTAClick = (message) => {
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Swipe handlers só para mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const swipeHandlers = useSwipe({
    onSwipeLeft: nextBanner,
    onSwipeRight: prevBanner,
  });

  return (
    <section
      id="hero"
      className="relative h-[600px] md:h-[700px] overflow-hidden bg-[#0a4b3c]"
      {...(isMobile ? swipeHandlers : {})}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBanner}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0 w-full h-full"
        >
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 bg-black/60 z-10" />
          <img
            src={banners[currentBanner].image}
            alt={banners[currentBanner].title}
            className="w-full h-full object-cover"
          />
          
          {/* Content */}
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="container mx-auto px-4 pt-16">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="text-white max-w-2xl"
                >
                  <motion.div
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#c99b36]/20 backdrop-blur-sm rounded-full mb-6 border border-[#c99b36]/30"
                  >
                    {React.createElement(banners[currentBanner].icon, { className: "w-5 h-5 text-[#c99b36]" })}
                    <span className="text-[#c99b36] font-semibold text-sm uppercase tracking-wider">TALC Assessoria Contábil</span>
                  </motion.div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white drop-shadow-md">
                    {banners[currentBanner].title}
                  </h1>
                  
                  <p className="text-lg md:text-xl mb-8 text-gray-100 leading-relaxed max-w-xl drop-shadow">
                    {banners[currentBanner].subtitle}
                  </p>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleCTAClick(banners[currentBanner].whatsappMessage)}
                    className="bg-[#c99b36] hover:bg-[#b0862b] text-white px-8 py-4 rounded-lg font-bold text-lg flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-[#c99b36]/40"
                  >
                    {banners[currentBanner].cta}
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons - só desktop */}
      {(!isMobile) && (
        <div className="absolute z-30 bottom-8 right-8 flex gap-4">
          <button 
            onClick={prevBanner}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all border border-white/20"
          >
            <ChevronLeft size={24} />
          </button>
          <button 
            onClick={nextBanner}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all border border-white/20"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      {/* Slide Indicators */}
      <div className="absolute z-30 bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
        {banners.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentBanner(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentBanner === index ? 'w-8 bg-[#c99b36]' : 'w-2 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;